import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../api/remembrApi';
import type { RootState } from '../../store/store';
import type {
  NotificationItem,
  NotificationListResponse,
  NotificationListParams,
} from '../../api/types';
// FE-213 — cross-slice listener: when the user successfully accepts
// a Group invite, mark the corresponding CREW_INVITE_RECEIVED
// notification(s) as read locally so the Dashboard's "Recent
// activity" stops re-displaying the dismissed card on refresh.
// Importing the thunk action creator is safe — groupsSlice does NOT
// import from notificationsSlice, so there's no cycle.
import { acceptGroupInvite } from '../sharing/groupsSlice';

/**
 * Notifications slice — Sprint 8 / FE-127 + FE-128 (backend#151).
 *
 * Server-side fan-out (BE-151) writes a row to the `notifications`
 * table after every relevant domain event (share created, invite
 * accepted, borrow approved, custody recalled, etc.). The FE reads
 * those rows through five endpoints; this slice caches them.
 *
 * Architecture (matches the user's earlier question):
 *   - Notification persistence + fan-out  ⇒ server (BE-151)
 *   - List + read state                   ⇒ server endpoints
 *   - Badge unread count                  ⇒ client polls server
 *   - CTA actions (Approve / Claim / etc.) ⇒ EXISTING domain
 *     endpoints (e.g. /groups/accept-invite). The notification
 *     row is a surface, not the action itself. Server marks rows
 *     read as a side-effect of any matching domain action, so
 *     the badge clears regardless of where the user actioned.
 *
 * State shape:
 *   - byId:           keyed cache of every fetched notification
 *   - listOrder:      ids in display order (newest first)
 *   - unreadCount:    server-authoritative; updated by every
 *                     successful list/poll/mark-read response
 *   - total:          total rows the server knows about
 *   - hasMore:        whether pagination has more pages
 *   - nextOffset:     cursor for the next page
 *   - loading*:       fine-grained spinners per surface
 *   - lastPolledAt:   timestamp of the last unread-count refresh
 *   - lastListLoaded: timestamp of the last full list fetch
 */

interface NotificationsState {
  byId: Record<number, NotificationItem>;
  listOrder: number[];
  unreadCount: number;
  total: number;
  hasMore: boolean;
  nextOffset: number;
  loadingList: boolean;
  loadingMore: boolean;
  loadingUnread: boolean;
  markingAllRead: boolean;
  /** Per-id micro-spinners for mark-read / dismiss optimistic UI. */
  pendingActionById: Record<number, 'mark-read' | 'dismiss' | undefined>;
  listError: string | null;
  /** Polling support — guards against runaway polling. */
  lastPolledAt: number | null;
  lastListLoadedAt: number | null;
}

const initialState: NotificationsState = {
  byId: {},
  listOrder: [],
  unreadCount: 0,
  total: 0,
  hasMore: false,
  nextOffset: 0,
  loadingList: false,
  loadingMore: false,
  loadingUnread: false,
  markingAllRead: false,
  pendingActionById: {},
  listError: null,
  lastPolledAt: null,
  lastListLoadedAt: null,
};

/* ──────────────────────────────────────────────────────────────────────
 * Thunks
 * ────────────────────────────────────────────────────────────────────── */

/** Fetch first page (or refresh from page 0). Pass `mode: 'append'`
 *  to load the next page; defaults to 'replace'. */
export const loadNotifications = createAsyncThunk<
  { data: NotificationListResponse; mode: 'replace' | 'append' },
  { params?: NotificationListParams; mode?: 'replace' | 'append' } | undefined,
  { rejectValue: string }
>(
  'notifications/loadNotifications',
  async (arg, { rejectWithValue, getState }) => {
    const mode = arg?.mode ?? 'replace';
    // getState is loosely typed here so this slice stays usable from
    // partial-store unit tests (state: RootState in the thunk type
    // makes Redux dispatch reject anything but the full store).
    const root = getState() as { notifications?: NotificationsState };
    const slice = root.notifications;
    const offset =
      mode === 'append' && slice
        ? slice.nextOffset
        : (arg?.params?.offset ?? 0);
    try {
      const data = await api.getNotifications({
        limit: 20,
        ...arg?.params,
        offset,
      });
      return { data, mode };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to load notifications',
      );
    }
  },
);

/** Cheap polled endpoint — count only, no rows. NotificationBell
 *  calls this every 60s while mounted. Server returns `{ count }`
 *  per BE-151 wire spec; we expose it as a number. */
export const refreshUnreadCount = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>(
  'notifications/refreshUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const data = await api.getUnreadNotificationCount();
      return data.count;
    } catch (err: any) {
      // Cancelled requests (rapid mount/unmount) shouldn't surface.
      if (
        err?.code === 'ERR_CANCELED' ||
        err?.name === 'CanceledError'
      ) {
        return rejectWithValue('canceled');
      }
      return rejectWithValue(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to refresh unread count',
      );
    }
  },
);

/** Mark a single row read. Optimistic — slice flips `read_at` +
 *  decrements `unreadCount` in `markRead.pending`, server returns
 *  204 to confirm (synthesized to `{ notification_id, read_at }`
 *  by the API client). On reject, slice reverts the flip.
 *
 *  Server also auto-marks rows read when the user actions the
 *  underlying domain event (e.g. accepts an invite); calling this
 *  explicitly is for the case where the user just wants to clear
 *  the badge without taking action.
 *
 *  Note: server idempotently preserves the original `read_at`
 *  timestamp on re-read, so a duplicate dispatch is harmless. */
export const markRead = createAsyncThunk<
  { id: number; readAt: string },
  number,
  { rejectValue: string }
>(
  'notifications/markRead',
  async (id, { rejectWithValue }) => {
    try {
      const data = await api.markNotificationRead(id);
      return { id: data.notification_id, readAt: data.read_at };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to mark notification read',
      );
    }
  },
);

/** Mark every unread row read in one call. Used by the
 *  NotificationsScreen "Mark all read" header action.
 *
 *  API client sends `before = new Date().toISOString()` per
 *  backend recommendation — preserves real-time unread events
 *  that landed during the screen render. Server returns
 *  `{ updated: N }`. */
export const markAllRead = createAsyncThunk<
  { markedCount: number },
  void,
  { rejectValue: string }
>(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      const data = await api.markAllNotificationsRead();
      return { markedCount: data.updated };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to mark all notifications read',
      );
    }
  },
);

/** Dismiss (soft-delete) a notification. Optimistic — removed from
 *  list immediately. */
export const dismiss = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: string }
>(
  'notifications/dismiss',
  async (id, { rejectWithValue }) => {
    try {
      await api.dismissNotification(id);
      return { id };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to dismiss notification',
      );
    }
  },
);

/* ──────────────────────────────────────────────────────────────────────
 * Slice
 * ────────────────────────────────────────────────────────────────────── */

const slice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /** Reset list on logout. */
    resetNotifications: () => initialState,

    /** Increment the badge by N without a full refetch. Used when
     *  another slice receives a server event that should bump the
     *  count (e.g. WebSocket / SSE in a future phase). For Sprint 8
     *  this is unused; polling handles it. Kept for forward-compat. */
    bumpUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = Math.max(0, state.unreadCount + action.payload);
    },

    /** Optimistic local mark-read. Called when a domain action
     *  (e.g. acceptInvite) succeeds — flips state before the
     *  server's mirroring update arrives via the next poll. */
    optimisticMarkRead: (state, action: PayloadAction<number>) => {
      const row = state.byId[action.payload];
      if (row && !row.read_at) {
        row.read_at = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
  },
  extraReducers: (builder) => {
    /* loadNotifications ────────────────────────────────── */
    builder.addCase(loadNotifications.pending, (state, action) => {
      const mode = action.meta.arg?.mode ?? 'replace';
      if (mode === 'append') state.loadingMore = true;
      else state.loadingList = true;
      state.listError = null;
    });
    builder.addCase(loadNotifications.fulfilled, (state, action) => {
      const { data, mode } = action.payload;
      const requestedOffset = action.meta.arg?.params?.offset;
      state.loadingList = false;
      state.loadingMore = false;
      state.total = data.total;
      state.unreadCount = data.unread_count;
      state.lastListLoadedAt = Date.now();

      // Server doesn't echo `limit/offset/has_more` (BE-151 spec).
      // We derive both client-side. `nextOffset` advances by the
      // page we just received; `hasMore` compares it to total.
      const startOffset =
        mode === 'append' ? state.nextOffset : (requestedOffset ?? 0);
      state.nextOffset = startOffset + data.notifications.length;
      state.hasMore = state.nextOffset < data.total;

      // Merge rows into byId — always overwrites with the freshest
      // copy (read_at could have flipped server-side).
      for (const row of data.notifications) {
        state.byId[row.id] = row;
      }

      if (mode === 'replace') {
        state.listOrder = data.notifications.map((r) => r.id);
      } else {
        // Append page — dedupe in case of overlap during fast scrolls.
        const seen = new Set(state.listOrder);
        for (const r of data.notifications) {
          if (!seen.has(r.id)) {
            state.listOrder.push(r.id);
            seen.add(r.id);
          }
        }
      }
    });
    builder.addCase(loadNotifications.rejected, (state, action) => {
      state.loadingList = false;
      state.loadingMore = false;
      state.listError = action.payload ?? 'Failed to load';
    });

    /* refreshUnreadCount ───────────────────────────────── */
    builder.addCase(refreshUnreadCount.pending, (state) => {
      state.loadingUnread = true;
    });
    builder.addCase(refreshUnreadCount.fulfilled, (state, action) => {
      state.loadingUnread = false;
      state.unreadCount = action.payload;
      state.lastPolledAt = Date.now();
    });
    builder.addCase(refreshUnreadCount.rejected, (state) => {
      state.loadingUnread = false;
      // Silent — bell keeps last known count to avoid flicker.
    });

    /* markRead — optimistic ───────────────────────────── */
    builder.addCase(markRead.pending, (state, action) => {
      const id = action.meta.arg;
      const row = state.byId[id];
      if (row && !row.read_at) {
        state.pendingActionById[id] = 'mark-read';
        // Optimistic flip — we'll overwrite with server's read_at
        // when fulfilled lands. If rejected, we undo below.
        row.read_at = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    });
    builder.addCase(markRead.fulfilled, (state, action) => {
      const { id, readAt } = action.payload;
      delete state.pendingActionById[id];
      // Overwrite the optimistic timestamp with the server-synthesized
      // one (currently client-now since BE returns 204; will be the
      // server's authoritative value when the next list fetch lands).
      // Server does not return the new unread count — slice already
      // decremented it optimistically in `markRead.pending`.
      if (state.byId[id]) {
        state.byId[id].read_at = readAt;
      }
    });
    builder.addCase(markRead.rejected, (state, action) => {
      const id = action.meta.arg;
      delete state.pendingActionById[id];
      // Revert optimistic flip.
      const row = state.byId[id];
      if (row) {
        row.read_at = null;
        state.unreadCount += 1;
      }
    });

    /* markAllRead ──────────────────────────────────────── */
    builder.addCase(markAllRead.pending, (state) => {
      state.markingAllRead = true;
    });
    builder.addCase(markAllRead.fulfilled, (state) => {
      state.markingAllRead = false;
      const nowIso = new Date().toISOString();
      for (const id of state.listOrder) {
        const row = state.byId[id];
        if (row && !row.read_at) row.read_at = nowIso;
      }
      state.unreadCount = 0;
    });
    builder.addCase(markAllRead.rejected, (state) => {
      state.markingAllRead = false;
    });

    /* dismiss — optimistic ─────────────────────────────── */
    builder.addCase(dismiss.pending, (state, action) => {
      const id = action.meta.arg;
      state.pendingActionById[id] = 'dismiss';
      // Optimistically remove from order; restore on reject.
      state.listOrder = state.listOrder.filter((x) => x !== id);
      const row = state.byId[id];
      if (row && !row.read_at) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    });
    builder.addCase(dismiss.fulfilled, (state, action) => {
      const { id } = action.payload;
      delete state.pendingActionById[id];
      delete state.byId[id];
      state.total = Math.max(0, state.total - 1);
    });
    builder.addCase(dismiss.rejected, (state, action) => {
      const id = action.meta.arg;
      delete state.pendingActionById[id];
      // Restore — re-insert the id in roughly the right spot
      // (top of list is fine; next refresh corrects the order).
      if (state.byId[id]) {
        state.listOrder.unshift(id);
        const row = state.byId[id];
        if (row && !row.read_at) state.unreadCount += 1;
      }
    });

    /* FE-213 — Stale CREW_INVITE_RECEIVED card on Dashboard.
     *
     * Backend BE-204 lands the same fix server-side, but the
     * Dashboard's local cache is still stale until the next poll.
     * Optimistically mirror the accept here: any unread
     * CREW_INVITE_RECEIVED notification whose payload.invite_token
     * matches the thunk's `meta.arg` (the invite token) gets
     * marked read locally + unread count decrements. Server's next
     * poll converges on the same state.
     *
     * Why a matcher / .fulfilled listener instead of dispatching
     * from AcceptInviteScreen: the screen never re-renders after
     * a successful auto-accept (it navigates away), and pushing
     * the dispatch into the success handler also means we miss
     * boot-resume + email-signin-redirect paths. A slice-level
     * listener catches every successful accept regardless of
     * which surface drove it.
     */
    builder.addCase(acceptGroupInvite.fulfilled, (state, action) => {
      const acceptedToken = action.meta.arg;
      if (!acceptedToken) return;
      for (const id of Object.keys(state.byId)) {
        const row = state.byId[Number(id)];
        if (!row) continue;
        // FE-226 — accept both legacy and renamed type so the cleanup
        // works across the SG-18 cutover.
        if (
          row.type !== 'CREW_INVITE_RECEIVED' &&
          row.type !== 'GROUP_INVITE_RECEIVED'
        )
          continue;
        const rowToken = (row.payload?.invite_token as string | undefined);
        if (rowToken !== acceptedToken) continue;
        if (row.read_at) continue;
        row.read_at = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    });

    /* PR31 — companion fix: accept-invite REJECTED with the
     * structured `ALREADY_REDEEMED` code (404 "Invite not found or
     * already redeemed") means the user is already a member. The
     * notification card on Dashboard is stale and should NOT
     * re-prompt them. Same cleanup as the fulfilled branch above —
     * mark matching CREW_INVITE_RECEIVED rows read locally. */
    builder.addCase(acceptGroupInvite.rejected, (state, action) => {
      const payload = action.payload as unknown;
      const isStructured =
        payload && typeof payload === 'object' && 'code' in (payload as any);
      if (!isStructured) return;
      if ((payload as any).code !== 'ALREADY_REDEEMED') return;
      const token = action.meta.arg;
      if (!token) return;
      for (const id of Object.keys(state.byId)) {
        const row = state.byId[Number(id)];
        if (!row) continue;
        // FE-226 — accept both legacy and renamed type so the cleanup
        // works across the SG-18 cutover.
        if (
          row.type !== 'CREW_INVITE_RECEIVED' &&
          row.type !== 'GROUP_INVITE_RECEIVED'
        )
          continue;
        const rowToken = (row.payload?.invite_token as string | undefined);
        if (rowToken !== token) continue;
        if (row.read_at) continue;
        row.read_at = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    });
  },
});

export const { resetNotifications, bumpUnreadCount, optimisticMarkRead } =
  slice.actions;
export default slice.reducer;

/* ──────────────────────────────────────────────────────────────────────
 * Selectors
 * ────────────────────────────────────────────────────────────────────── */

export const selectUnreadCount = (s: RootState): number =>
  s.notifications.unreadCount;

/** Server-authoritative count, but capped at 99 for badge rendering
 *  (UI shows "99+" for anything higher). */
export const selectBadgeText = (s: RootState): string | null => {
  const c = s.notifications.unreadCount;
  if (c <= 0) return null;
  if (c > 99) return '99+';
  return String(c);
};

export const selectNotifications = (s: RootState): NotificationItem[] =>
  s.notifications.listOrder
    .map((id) => s.notifications.byId[id])
    .filter((x): x is NotificationItem => !!x);

export const selectIsLoadingList = (s: RootState): boolean =>
  s.notifications.loadingList;

export const selectIsLoadingMore = (s: RootState): boolean =>
  s.notifications.loadingMore;

export const selectHasMore = (s: RootState): boolean =>
  s.notifications.hasMore;

export const selectListError = (s: RootState): string | null =>
  s.notifications.listError;

export const selectIsMarkingAllRead = (s: RootState): boolean =>
  s.notifications.markingAllRead;

/** Top-5 most recent for the bell popover (Sprint 8 spec: "max 5 in
 *  the summary surface, full list lives in NotificationsScreen"). */
export const selectTopFiveNotifications = (
  s: RootState,
): NotificationItem[] => selectNotifications(s).slice(0, 5);

/** Per-id pending action — drives micro-spinners on cards. */
export const selectPendingAction =
  (id: number) =>
  (s: RootState): 'mark-read' | 'dismiss' | undefined =>
    s.notifications.pendingActionById[id];

/* ──────────────────────────────────────────────────────────────────────
 * Sprint 8 / FE-130 — Share tab section selectors
 *
 * The Share tab's "Needs your attention" block surfaces a curated subset
 * of notifications where the user is expected to take an action
 * (accept/decline an invite, claim a share, approve a borrow,
 * acknowledge a recall). The "Recent activity" block surfaces the
 * informational rest (handoffs completed, items returned, claims
 * confirmed, etc.) — useful context without demanding action.
 *
 * These selectors are read by `CrewHubScreen` and any other surface
 * that wants the same split (e.g. a future ItemDetail "Recent activity"
 * mini-feed could reuse them).
 * ────────────────────────────────────────────────────────────────────── */

/** Notification types the user can directly action from a card.
 *  Kept as a Set for O(1) membership check inside selectors. */
const ACTION_REQUIRED_TYPES = new Set<string>([
  'SHARE_RECEIVED',
  'CREW_INVITE_RECEIVED',
  'GROUP_INVITE_RECEIVED', // FE-226 — SG-18 rename
  'BORROW_REQUEST_RECEIVED',
  'CUSTODY_RECALL',
]);

/** Top-5 unread notifications where the user is expected to act.
 *  Drives the "Needs your attention" block at the top of the Share
 *  tab. Once the user reads / dismisses / takes the action, the row
 *  drops out (either by `read_at` flipping or being removed from
 *  `listOrder` via dismiss).
 *
 *  Stays empty until `loadNotifications` has run — silent until
 *  there's data to show. */
export const selectActionRequiredNotifications = (
  s: RootState,
): NotificationItem[] =>
  selectNotifications(s)
    .filter((n) => !n.read_at && ACTION_REQUIRED_TYPES.has(n.type))
    .slice(0, 5);

/** Count of all unread action-required rows (NOT capped at 5).
 *  Used for the "(N)" badge next to the "Needs your attention"
 *  section header. */
export const selectActionRequiredCount = (s: RootState): number =>
  selectNotifications(s).filter(
    (n) => !n.read_at && ACTION_REQUIRED_TYPES.has(n.type),
  ).length;

/** Top-5 informational notifications — everything that is NOT
 *  action-required. Drives the "Recent activity" block at the bottom
 *  of the Share tab (and is reusable from any other "activity feed"
 *  surface that wants the informational subset).
 *
 *  Includes both unread and read rows — the user wants the recency
 *  signal, not the action signal, here. */
export const selectInformationalNotifications = (
  s: RootState,
): NotificationItem[] =>
  selectNotifications(s)
    .filter((n) => !ACTION_REQUIRED_TYPES.has(n.type))
    .slice(0, 5);
